import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Ware } from '../types';
import wareService from '../services/wareService';
import { Icon, PageHeader, LoadingScreen, EmptyState, FilterStand, FilterZuruecksetzen, AnlegenButton, filterAktiv, fehlerText } from '../components/ui';
import { useStueckweise } from '../utils/stueckweise';
import { markiereNeu, ankunft, frischerIndex } from '../utils/ankunft';

const EINHEITEN = ['L', 'm', 'kg', 'Stk', 'm²', 'm³'];

const Waren: React.FC = () => {
  const [waren, setWaren] = useState<Ware[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingWare, setEditingWare] = useState<Ware | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({ artikelbezeichnung: '', einheit: 'Stk', erinnerungIntervallMonate: '' as string });
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => { loadWaren(); }, []);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadWaren = async () => {
    try {
      setWaren(await wareService.getAll());
    } catch (err) { toast.error(fehlerText(err, 'Artikel konnten nicht geladen werden')); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const submitData = {
      artikelbezeichnung: formData.artikelbezeichnung,
      einheit: formData.einheit,
      erinnerungIntervallMonate: formData.erinnerungIntervallMonate ? parseInt(formData.erinnerungIntervallMonate) : null,
    };
    try {
      if (editingWare) {
        await wareService.update(editingWare.id, submitData);
        markiereNeu(`ware-${editingWare.id}`);
        toast.success('Artikel gespeichert');
      } else {
        const angelegt = await wareService.create(submitData);
        if (angelegt?.id) markiereNeu(`ware-${angelegt.id}`);
        toast.success('Artikel angelegt');
      }
      resetForm();
      await loadWaren();
    } catch (err: any) {
      toast.error(fehlerText(err, 'Artikel konnte nicht gespeichert werden'));
    }
  };

  const handleEdit = (ware: Ware) => {
    setOpenMenuId(null);
    setEditingWare(ware);
    setFormData({
      artikelbezeichnung: ware.artikelbezeichnung,
      einheit: ware.einheit,
      erinnerungIntervallMonate: ware.erinnerungIntervallMonate ? String(ware.erinnerungIntervallMonate) : '',
    });
    setShowForm(true);
  };

  const handleDelete = async (ware: Ware) => {
    setOpenMenuId(null);
    if (window.confirm(`Artikel „${ware.artikelbezeichnung}“ löschen?`)) {
      try {
        await wareService.delete(ware.id);
        toast.success('Artikel gelöscht');
        await loadWaren();
      } catch (err: any) {
        toast.error(fehlerText(err, 'Artikel konnte nicht gelöscht werden'));
      }
    }
  };

  const resetForm = () => {
    setFormData({ artikelbezeichnung: '', einheit: 'Stk', erinnerungIntervallMonate: '' });
    setEditingWare(null);
    setShowForm(false);
  };

  const filteredWaren = useMemo(() => waren.filter(w =>
    w.artikelbezeichnung.toLowerCase().includes(searchTerm.toLowerCase())
  ), [waren, searchTerm]);
  const { sichtbar, Ende, zeigeBis } = useStueckweise(filteredWaren);
  // Aus der Suche angesprungen: auch weiter hinten liegende Artikel rendern, damit sie aufleuchten.
  useEffect(() => {
    const i = frischerIndex(filteredWaren, (w) => `ware-${w.id}`);
    if (i >= 0) zeigeBis(i);
  }, [filteredWaren, zeigeBis]);
  const filterIstAktiv = searchTerm.trim() !== '';

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="p-4 md:p-6">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-gray-100 pb-3 -mx-4 px-4 -mt-4 pt-4 md:-mx-6 md:px-6 md:-mt-6 md:pt-6">
        <PageHeader
          title="Artikel"
          actions={<AnlegenButton onClick={() => setShowForm(true)}>Artikel anlegen</AnlegenButton>}
          className="mb-3"
        />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="relative flex-1 min-w-[12rem]">
            <Icon.Search className={`pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 ${filterIstAktiv ? 'text-brand-600' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Artikel suchen …"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full p-2 pl-8 border border-gray-300 rounded-lg text-sm ${filterIstAktiv ? filterAktiv : ''}`}
            />
          </div>
          <FilterStand treffer={filteredWaren.length} gesamt={waren.length} einheit="Artikel" aktiv={filterIstAktiv} onZuruecksetzen={() => setSearchTerm('')} />
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="ui-fade fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="ui-pop bg-white rounded-lg p-5 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">
              {editingWare ? 'Artikel bearbeiten' : 'Artikel anlegen'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Artikelbezeichnung</label>
                <input
                  type="text"
                  required
                  value={formData.artikelbezeichnung}
                  onChange={(e) => setFormData({ ...formData, artikelbezeichnung: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="z. B. Chlor flüssig"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Einheit</label>
                <select
                  value={formData.einheit}
                  onChange={(e) => setFormData({ ...formData, einheit: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                >
                  {EINHEITEN.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Erinnerungsintervall (Monate)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.erinnerungIntervallMonate}
                  onChange={(e) => setFormData({ ...formData, erinnerungIntervallMonate: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="z. B. 24 (optional)"
                />
                <p className="text-xs text-gray-400 mt-1">Leer lassen, wenn keine Erinnerung gewünscht ist</p>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={resetForm}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm">
                  Abbrechen
                </button>
                <button type="submit"
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm">
                  {editingWare ? 'Speichern' : 'Artikel anlegen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Artikel List */}
      <div className="space-y-2">
        {sichtbar.map((ware) => {
          const isMenuOpen = openMenuId === ware.id;
          const a = ankunft(`ware-${ware.id}`);

          return (
            <div key={ware.id} ref={a.ref} className={`bg-white rounded-lg shadow-sm border border-gray-100 ${a.className}`}>
              <div className="flex items-center p-3 md:p-4">
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{ware.artikelbezeichnung}</div>
                </div>

                {/* Erinnerung badge */}
                {ware.erinnerungIntervallMonate && (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 mr-2">
                    <Icon.Glocke className="w-3 h-3" />
                    {ware.erinnerungIntervallMonate} Mon.
                  </span>
                )}

                {/* Einheit badge */}
                <span className="flex-shrink-0 inline-flex px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 mr-2">
                  {ware.einheit}
                </span>

                {/* Three-dot menu */}
                <div className="relative flex-shrink-0" ref={isMenuOpen ? menuRef : undefined}>
                  <button
                    onClick={() => setOpenMenuId(isMenuOpen ? null : ware.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Weitere Aktionen"
                  >
                    <Icon.Mehr className="w-5 h-5" />
                  </button>

                  {isMenuOpen && (
                    <div className="ui-fade absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-30 py-1">
                      <button
                        onClick={() => handleEdit(ware)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Icon.Edit className="w-4 h-4 text-gray-400" />
                        Bearbeiten
                      </button>
                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        onClick={() => handleDelete(ware)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Icon.Trash className="w-4 h-4" />
                        Löschen
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {Ende}
      </div>

      {filteredWaren.length === 0 && (
        filterIstAktiv ? (
          <EmptyState
            icon={<Icon.Search className="h-6 w-6" />}
            title="Keine Artikel gefunden"
            hint={`Kein Artikel passt zu „${searchTerm}“.`}
            action={<FilterZuruecksetzen onClick={() => setSearchTerm('')} />}
          />
        ) : (
          <EmptyState
            icon={<Icon.Paket className="h-6 w-6" />}
            title="Noch keine Artikel"
            hint="Artikel werden in Berichten als Material ausgewählt."
            action={<AnlegenButton onClick={() => setShowForm(true)}>Artikel anlegen</AnlegenButton>}
          />
        )
      )}
    </div>
  );
};

export default Waren;
